/**
 * Problem-Solving & Reasoning: multi-step questions, logic and reasoning, interpreting word problems.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const makeDistractors = (correctNum, count = 3) => {
    const correct = String(correctNum);
    const seen = new Set([correct]);
    const out = [];
    const offsets = [1, 2, 3, 5, 10, -1, -2, -5];
    while (out.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const d = String(correctNum + off);
        if (d !== correct && !seen.has(d)) {
            seen.add(d);
            out.push(d);
        }
    }
    return out;
};

/** Multi-step word problems — analyse and apply two steps */
export const generateMultiStepReasoningQuestion = (difficulty = "normal") => {
    const range = difficulty === "easy" ? [3, 15] : difficulty === "hard" ? [10, 40] : [5, 25];
    const roll = () => Math.floor(Math.random() * (range[1] - range[0] + 1)) + range[0];

    const templates = [
        {
            q: (a, b, c) => `A shop has ${a} apples. It sells ${b}, then gets ${c} more. How many apples now?`,
            ans: (a, b, c) => a - b + c,
            gen: () => {
                const a = roll();
                const b = Math.min(roll(), a - 1);
                const c = roll();
                return [a, b, c];
            },
        },
        {
            q: (a, b, c) => `Tom has ${a} boxes of ${b} pencils. He gives ${c} to his friend. How many pencils does he have left?`,
            ans: (a, b, c) => a * b - c,
            gen: () => {
                const a = Math.floor(Math.random() * 4) + 2;
                const b = Math.floor(Math.random() * 8) + 4;
                const c = Math.min(roll(), a * b - 1);
                return [a, b, c];
            },
        },
        {
            q: (a, b, c) => `There are ${a} books on a shelf. ${b} are removed and then ${c} are added. How many books are on the shelf now?`,
            ans: (a, b, c) => a - b + c,
            gen: () => {
                const a = roll();
                const b = Math.min(roll(), a);
                const c = roll();
                return [a, b, c];
            },
        },
    ];

    const idx = Math.floor(Math.random() * templates.length);
    const t = templates[idx];
    const [x, y, z] = t.gen();
    const finalAnswer = t.ans(x, y, z);
    const question = t.q(x, y, z);
    const options = shuffle([String(finalAnswer), ...makeDistractors(finalAnswer, 3)]);
    return {
        question,
        answer: String(finalAnswer),
        options,
        type: "multi-step-reasoning",
        mode: "multi-step",
        visualType: "multi-step",
    };
};

/** Logic/reasoning — work backwards or find the missing number */
export const generateLogicReasoningQuestion = (difficulty = "normal") => {
    const questions = [
        { q: "A number is multiplied by 5, then 10 is added. The result is 35. What was the number?", answer: "5" },
        { q: "I think of a number, add 7 and get 15. What is the number?", answer: "8" },
        { q: "Double a number is 24. What is the number?", answer: "12" },
        { q: "A number minus 12 equals 8. What is the number?", answer: "20" },
        { q: "I think of a number, multiply by 3 and get 27. What is the number?", answer: "9" },
    ];
    const item = questions[Math.floor(Math.random() * questions.length)];
    const options = shuffle([item.answer, ...makeDistractors(Number(item.answer), 3)]);
    return {
        question: item.q,
        answer: item.answer,
        options,
        type: "logic-reasoning",
        mode: "logic",
        visualType: "logic-work-back",
    };
};

export const generateProblemSolvingQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "multi-step-reasoning":
            return generateMultiStepReasoningQuestion(difficulty);
        case "logic-reasoning":
            return generateLogicReasoningQuestion(difficulty);
        default:
            return generateMultiStepReasoningQuestion(difficulty);
    }
};
